import React from 'react';
import type { ReportData } from '../tokens/types';
import { DataTable, Box, Stack, Row, T, Divider } from '../primitives/Universal';
import { fmtPct, truncate } from './formatUtils';

const WEDGE_COLORS = ['#6021B3', '#8B5CF6', '#B794F4', '#D6BCFA', '#E9D5FF', '#F3E8FF'];

// Skill rule 25: every numeric value that lands in a yoga style prop (flex,
// width, height, …) must be guarded. `r.percent` comes from upstream API
// normalisation and can legitimately arrive as 0 or arithmetically polluted
// (NaN / Infinity) when a row's stake is missing. Pass-through to `flex={…}`
// crashes pdfkit with `unsupported number: -1.806e+21` because yoga's flex
// space distribution divides by the sum of flex values — a sum of 0 (every
// child = 0) or any NaN child poisons the whole row.
const safePct = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Body of the Shareholding pattern — table + top-holders legend, no Section wrapper
 * and no header text. Reused by the merged Structure & Relations module so the
 * existing UI is preserved verbatim. Wrappers (standalone module / S&R) own the
 * heading + "As of …" line.
 */
export const ShareholdingBlock: React.FC<{ data: ReportData }> = ({ data }) => {
    const s = data.shareholding;
    if (!s || !s.rows.length) return null;

    const sorted = [...s.rows].sort((a, b) => safePct(b.percent) - safePct(a.percent));
    // Drop 0-percent rows from the top-5 legend — a legend row with 0% is
    // visually meaningless and lets the wedge bar degenerate (sum of zeros).
    const top = sorted.slice(0, 5).filter((r) => safePct(r.percent) > 0);
    const total = s.rows.reduce((acc, r) => acc + safePct(r.percent), 0);
    // The Shareholder column gets ~58% of body width (the rest is split between
    // Type at 28% and % stake at 14%). At our 8.5pt body size that's ~50 chars
    // before the cell hits the next column; long fund / vehicle names like
    // "MULTIPLES PRIVATE EQUITY FUND IV - INDIA INVESTMENT VEHICLE" used to spill
    // into the Type column and shove the row baseline. Truncating the name + the
    // already-snug type label keeps every row on a single line.
    const tableRows = sorted.slice(0, 12).map((r) => ({
        name: truncate(r.holderName, 48),
        type: truncate(r.holderType, 30),
        pct: fmtPct(r.percent, 2),
    }));

    return (
        <>
            <Row gap={16} align="stretch">
                <Box flex={1}>
                    <DataTable
                        columns={[
                            { key: 'name', header: 'Shareholder' },
                            // Type column was 18% — too narrow for values like "Managing
                            // Director" or "Independent Director" which wrapped onto two
                            // lines and visually drifted the row baselines. 28% absorbs
                            // every realistic holderType without wrap.
                            { key: 'type', header: 'Type', width: '28%' },
                            { key: 'pct', header: '% stake', width: '14%', align: 'right' },
                        ]}
                        rows={tableRows}
                    />
                </Box>

                <Box width={170}>
                    <T size="micro" tone="muted" transform="uppercase" letterSpacing={0.8} marginBottom={6}>
                        Top holders
                    </T>

                    <Row height={10} borderRadius={5} overflow="hidden">
                        {top.map((r, i) => (
                            // Math.max(…, 0.001) is the load-bearing guard: even
                            // a legitimately 0-stake row that survived the filter
                            // above would yield flex=0 here. Yoga tolerates a
                            // small positive float; it crashes on NaN or a
                            // whole-row sum of 0.
                            <Box
                                key={`bar-${r.holderName}-${r.holderType}-${i}`}
                                flex={Math.max(safePct(r.percent), 0.001)}
                                background={WEDGE_COLORS[i % WEDGE_COLORS.length]}
                            />
                        ))}
                    </Row>

                    <Stack marginTop={8} gap={3}>
                        {top.map((r, i) => (
                            // skill rule 9 corollary: micro + micro/bold siblings need lineHeight:1 to share line-box
                            <Row key={`legend-${r.holderName}-${r.holderType}-${i}`} align="center" gap={6}>
                                <Box width={8} height={8} background={WEDGE_COLORS[i % WEDGE_COLORS.length]} borderRadius={2} />
                                <T size="micro" tone="muted" flex={1} lineHeight={1}>
                                    {r.holderName}
                                </T>
                                {/* Mono font for percentages so the decimal point
                                    renders clearly at micro size (sansBold's dot
                                    at 6.5pt can read as a stray gap, making
                                    "57.0%" look like "570%"). Drop trailing .0
                                    for whole-number stakes to avoid the issue
                                    entirely when possible. */}
                                <T size="micro" family="mono" lineHeight={1}>
                                    {Number.isInteger(r.percent) ? `${r.percent}%` : fmtPct(r.percent, 1)}
                                </T>
                            </Row>
                        ))}
                    </Stack>

                    <Divider marginVertical={8} />

                    <T size="micro" tone="mutedSoft">
                        Total reported · {fmtPct(total, 2)}
                    </T>
                </Box>
            </Row>
        </>
    );
};

// NOTE: the standalone `shareholdingModule` was removed — its content is now
// rendered inline inside `RelatedParties` (Structure & Relations module). Only
// the `ShareholdingBlock` body export remains and is consumed there.
